<template>
	<div class="dbb">

		<!-- ── Config (repliable) ─────────────────────────────────── -->
		<div class="dbb-config">
			<div class="dbb-config-toggle" @click="show_config = !show_config">
				<i class="fas fa-cog"></i> Configuration
				<i :class="show_config ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" style="float:right"></i>
			</div>
			<div v-show="show_config" class="dbb-config-body">
				<div class="dbb-field">
					<label>Titre du tableau de bord</label>
					<b-input v-model="title" placeholder="Ex : Accueil" />
				</div>
				<div class="dbb-config-row">
					<div class="dbb-field">
						<label>Actif</label>
						<b-form-checkbox v-model="active" switch>
							{{ active ? 'Oui' : 'Non' }}
						</b-form-checkbox>
					</div>
					<div class="dbb-field">
						<label>Ordre d'affichage <small class="text-muted">(plus petit = premier)</small></label>
						<b-input v-model.number="order" type="number" min="0" style="max-width:120px" />
					</div>
				</div>
				<div class="dbb-field">
					<label>Table utilisateur (conditions d'affichage)</label>
					<b-form-select v-model="config.user_table" :options="tables" @change="update_user_table" />
				</div>
				<div v-show="config.user_table" class="dbb-config-user-table">
					<div class="dbb-field">
						<label>Colonne uid</label>
						<b-input v-model="config.user_table_uid_column" />
					</div>
					<div class="dbb-field">
						<label>Colonne identifiant</label>
						<b-input v-model="config.user_table_identifiant_column" />
					</div>
				</div>
			</div>
		</div>

		<!-- ── Corps principal : canvas + éditeur ────────────────── -->
		<div class="dbb-main">

			<!-- Canvas visuel -->
			<div class="dbb-canvas" :class="{ 'dbb-locked': codegenFocusMode }">

				<!-- État vide -->
				<div v-if="blocks.length === 0" class="dbb-empty">
					<i class="fas fa-th-large dbb-empty-icon"></i>
					<p>Aucun bloc pour le moment.</p>
					<b-button variant="primary" @click="add_line_with_block">
						<i class="fas fa-plus"></i> Créer la première ligne
					</b-button>
				</div>

				<!-- Lignes draggables -->
				<draggable v-else :list="blocks" handle=".dbb-line-handle" @end="onLineDragEnd" class="dbb-lines">
					<div v-for="(line_blocks, id_line) in blocks" :key="line_blocks.uid" class="dbb-line">

						<!-- En-tête de ligne -->
						<div class="dbb-line-header">
							<span class="dbb-line-handle" title="Déplacer la ligne">
								<i class="fas fa-grip-horizontal"></i>
							</span>
							<span class="dbb-line-label">Ligne {{ id_line + 1 }}</span>
							<i v-if="lines[id_line] && lines[id_line].conditions && lines[id_line].conditions.rules_sql"
							   class="fas fa-filter dbb-line-filter-active" title="Conditions actives"></i>
							<div class="dbb-line-actions">
						<b-badge v-if="lineSize(id_line) > 12" variant="danger" class="mr-1" :title="'Total : ' + lineSize(id_line) + '/12'">
							<i class="fas fa-exclamation-triangle"></i> {{ lineSize(id_line) }}/12
						</b-badge>
								<b-button variant="link" size="sm" title="Conditions d'affichage"
										  :class="{ 'dbb-btn-active': view_id === (lines[id_line] && lines[id_line].uid) }"
										  @click="set_view_id(lines[id_line].uid)">
									<i class="fas fa-filter"></i>
								</b-button>
								<b-button variant="link" size="sm" title="Ajouter un bloc" @click="addAndSelect(id_line)">
									<i class="fas fa-plus"></i>
								</b-button>
								<b-button variant="link" size="sm" title="Supprimer la ligne" class="dbb-btn-danger"
										  @click="askDelLine(id_line)">
									<i class="fas fa-trash"></i>
								</b-button>
							</div>
						</div>

						<!-- Blocs draggables (inter-lignes via group="blocks") -->
						<draggable
							:list="line_blocks.blocks"
							group="blocks"
							@end="onBlockDragEnd"
							class="dbb-blocks-row"
							:class="{ 'dbb-blocks-row--empty': line_blocks.blocks.length === 0 }"
						>
							<div
								v-for="(block, idx) in line_blocks.blocks"
								:key="block.uid"
								class="dbb-block"
								:class="['dbb-block--' + (block.type || 'none'), view_id === block.uid ? 'dbb-block--selected' : '']"
								:style="{ flex: block.size > 0 ? block.size : 1 }"
								@click="set_view_id(block.uid)"
							>
								<span :class="'dbb-type-badge dbb-type-badge--' + (block.type || 'none')">{{ typeName(block.type) }}</span>
								<div class="dbb-block-name">{{ block.name || '(sans nom)' }}</div>
								<div class="dbb-block-size-hint">{{ block.size > 0 ? block.size + '/12' : block.size === 0 ? '⊕ attaché' : 'auto' }}</div>
							</div>
						</draggable>
					</div>
				</draggable>

				<!-- Zone de dépôt → nouvelle ligne -->
				<draggable
					:list="new_line_blocks"
					group="blocks"
					@add="onDropToNewLine"
					class="dbb-new-line-zone"
				>
					<div v-if="new_line_blocks.length === 0" class="dbb-new-line-hint">
						<i class="fas fa-arrow-down"></i> Déposer ici pour créer une nouvelle ligne
					</div>
				</draggable>

				<!-- Bouton ajout bloc -->
				<div class="dbb-canvas-toolbar">
					<b-button variant="outline-secondary" size="sm" @click="add_line_with_block">
						<i class="fas fa-plus"></i> Nouveau bloc
					</b-button>
				</div>

			</div>

			<!-- Panneau éditeur (tous les éditeurs rendus, v-show contrôle la visibilité) -->
			<div class="dbb-editor" :class="{ 'dbb-editor--open': !!view_id }">

				<!-- Placeholder si rien de sélectionné -->
				<div v-show="!view_id" class="dbb-editor-placeholder">
					<i class="fas fa-mouse-pointer"></i>
					<p>Cliquez sur un bloc ou <i class="fas fa-filter"></i> d'une ligne pour l'éditer.</p>
				</div>

				<!-- Éditeur de conditions de ligne -->
				<div v-for="(line, id_line) in lines" :key="'line-editor-' + line.uid" v-show="line.uid === view_id">
					<div class="dbb-editor-header">
						<i class="fas fa-layer-group"></i> Conditions — Ligne {{ id_line + 1 }}
					</div>
					<div class="dbb-editor-body">
						<queryBuilder :ref="'query' + line.uid" :columns="columns"></queryBuilder>
					</div>
				</div>

				<!-- Éditeur de bloc -->
				<div v-for="(line_blocks, id_line) in blocks" :key="'block-editors-' + line_blocks.uid">
					<div
						v-for="(block, idx) in line_blocks.blocks"
						:key="'block-editor-' + block.uid"
						v-show="block.uid === view_id"
					>
						<div class="dbb-editor-header">
							<span :class="'dbb-type-badge dbb-type-badge--' + (block.type || 'none')">{{ typeName(block.type) }}</span>
							{{ block.name || 'nouveau bloc' }}
						</div>
						<div class="dbb-editor-body">

							<div :class="{ 'dbb-locked': codegenFocusMode }">

							<div class="dbb-field">
								<label>Nom</label>
								<b-input v-model="block.name" />
							</div>

							<div class="dbb-field">
								<label>Type</label>
								<b-select v-model="block.type" :options="types" />
							</div>

							<div class="dbb-field">
								<label>Taille
									<small class="text-muted ml-2" v-if="block.size > 0">
										{{ block.size }}/12 (~{{ Math.round(block.size / 12 * 100) }}%)
									</small>
									<small class="text-muted ml-2" v-else-if="block.size === 0">attaché au précédent</small>
									<small class="text-muted ml-2" v-else>non défini</small>
								</label>
							<div class="dbb-size-selector" @mouseleave="hoverSize = 0">
								<div
									v-for="n in 12" :key="n"
									:class="[
										'dbb-size-cell',
										n <= (block.size || 0) ? 'dbb-size-cell--on' : '',
										hoverSize > 0 && n <= hoverSize && n > (block.size || 0) ? 'dbb-size-cell--hover' : '',
										hoverSize > 0 && n <= (block.size || 0) && n > hoverSize ? 'dbb-size-cell--hover-off' : ''
									]"
									@click="block.size = n"
									@mouseover="hoverSize = n"
										:title="n + '/12'"
									></div>
								</div>
								<b-button variant="link" size="sm" class="p-0 mt-1"
										  :class="{ 'font-weight-bold': !block.size && block.size !== 0 }"
										  @click="block.size = ''">
									↺ Auto
								</b-button>
								<b-button variant="link" size="sm" class="p-0 mt-1 ml-2"
										  :class="{ 'font-weight-bold': block.size === 0 }"
										  @click="block.size = 0">
									⊕ Attaché au bloc précédent
								</b-button>
							</div>

							<div v-if="block.size === 0" class="dbb-field">
								<label>Icône (Font Awesome 5)</label>
								<b-input v-model="block.icon" placeholder="ex: fas fa-user" />
								<span class="mt-1 d-inline-block">Rendu : <i :class="block.icon"></i></span>
							</div>

						<div class="dbb-field">
							<b-form-checkbox v-model="block.no_expand">Désactiver l'agrandissement</b-form-checkbox>
						</div>

							</div>

							<h6 class="dbb-params-title">Paramètres</h6>
							<textBuilder :params="block.params" :ref="block.uid" v-if="block.type === 'text'" @codegen-test-mode="onCodegenTestMode"></textBuilder>
							<dbBuilder :params="block.params" :ref="block.uid" v-if="block.type === 'db'"></dbBuilder>
							<notifsBuilder :params="block.params" :ref="block.uid" v-if="block.type === 'notifs'"></notifsBuilder>
							<actionsBuilder :params="block.params" :ref="block.uid" v-if="block.type === 'actions'"></actionsBuilder>
							<buttonBuilder :params="block.params" :ref="block.uid" v-if="block.type === 'button'"></buttonBuilder>
							<vueComponentBuilder :params="block.params" :ref="block.uid" v-if="block.type === 'vue_component'" @codegen-test-mode="onCodegenTestMode"></vueComponentBuilder>
							<tableBuilder :params="block.params" :ref="block.uid" v-if="block.type === 'table'"></tableBuilder>

							<div class="dbb-editor-del" :class="{ 'dbb-locked': codegenFocusMode }">
								<b-button variant="outline-danger" size="sm" @click="askDel(id_line, idx)">
									<i class="fas fa-trash"></i> Supprimer ce bloc
								</b-button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- ── Barre d'actions ─────────────────────────────────────── -->
		<div class="dbb-topbar">
			<b-button variant="primary" size="sm" @click="preview">
				<i class="fas fa-eye"></i> Prévisualiser
			</b-button>
			<test_as_user @change="onTestUserChange"></test_as_user>
		</div>

		<!-- Prévisualisation -->
		<dashboard ref="dashboard" :minimal="true" :test_jwt="test_user ? test_user.jwt : null" :test_user="test_user"></dashboard>

		<!-- Modal de confirmation -->
		<b-modal
			ref="confirm_modal"
			title="Confirmer la suppression"
			ok-variant="danger"
			ok-title="Supprimer"
			cancel-title="Annuler"
			@ok="doConfirm"
		>
			{{ confirm_message }}
		</b-modal>
	</div>
</template>

<script>
	import uniqueId from "lodash.uniqueid";
	import draggable from "vuedraggable";

	import store from "../store";
	import dashboard from "./Dashboard.vue";
	import database from "../services/database.js";
	import queryBuilder from "../components/queryBuilder.vue";
	import dbBuilder from "../components/dashboard/dbBuilder.vue";
	import textBuilder from "../components/dashboard/textBuilder.vue";
	import buttonBuilder from "../components/dashboard/buttonBuilder.vue";
	import notifsBuilder from "../components/dashboard/notifsBuilder.vue";
	import actionsBuilder from "../components/dashboard/actionsBuilder.vue";
	import vueComponentBuilder from "../components/dashboard/vueComponentBuilder.vue";
	import tableBuilder from "../components/dashboard/tableBuilder.vue";
	import test_as_user from "../components/TestAsUser.vue";

	var clone = require("clone");

	export default {
		name: "dashboard_builder",
		components: {
			dashboard,
			textBuilder,
			dbBuilder,
			notifsBuilder,
			actionsBuilder,
			draggable,
			queryBuilder,
			buttonBuilder,
			vueComponentBuilder,
			tableBuilder,
			test_as_user
		},
		props: {},
		data() {
			return {
				id: uniqueId(""),

				title: "",
				blocks: [],
				lines: [],

				// Zone de dépôt pour créer une nouvelle ligne par drag-and-drop
				new_line_blocks: [],

				types: [
					{ value: "text",          text: "Texte" },
					{ value: "db",            text: "Vue base de données" },
					{ value: "actions",       text: "Actions" },
					{ value: "notifs",        text: "Notifications" },
					{ value: "button",        text: "Bouton" },
					{ value: "vue_component", text: "Composant Vue.js" },
					{ value: "table",         text: "Base de données" }
				],

				typeLabels: {
					text: "Txt", db: "DB", actions: "Act",
					notifs: "Notif", button: "Btn", vue_component: "Vue", table: "Tab"
				},

				view_id: null,
				show_config: false,

				active: false,
				order: 0,

				// Confirmation suppression
				confirm_message: "",
				confirm_callback: null,

				columns: ["groups", "env", "nom", "prenom", "identifiant"],
				config: {
					user_table: "",
					user_table_uid_column: "",
					user_table_identifiant_column: ""
				},
				tables: [],
				hoverSize: 0,

				test_user: null,

				// Mode "test en place" du générateur de code (TODO_CODEGEN_CHAT.md
				// section 10) : activé par un bloc (textBuilder/vueComponentBuilder)
				// via l'event `codegen-test-mode`. Verrouille la navigation entre blocs
				// et les champs du bloc courant (nom/type/taille/suppression), mais PAS
				// le bouton "Prévisualiser" ni le rendu de prévisualisation.
				codegenFocusMode: false
			};
		},
		methods: {

			onTestUserChange(test_user) {
				this.test_user = test_user;
			},

			// Relaie l'état du mode test vers Library.vue (pour désactiver le bouton
			// "Enregistrer") et verrouille localement la navigation entre blocs.
			onCodegenTestMode(active) {
				this.codegenFocusMode = !!active;
				this.$emit('codegen-test-mode', this.codegenFocusMode);
			},

			// ── Taille de ligne ────────────────────────────────────────

			lineSize(id_line) {
				if (!this.blocks[id_line]) return 0;
				return this.blocks[id_line].blocks.reduce((sum, b) => {
					const s = parseInt(b.size);
					return sum + (s > 0 ? s : 0);
				}, 0);
			},

			// ── Helpers ────────────────────────────────────────────

			typeName(type) {
				return this.typeLabels[type] || (type ? type : "?");
			},

			// ── Sélection ─────────────────────────────────────────

			set_view_id(id) {
				this.view_id = id;
			},

			// ── Aperçu ────────────────────────────────────────────

			async preview() {
				const data = await this.save();
				await this.$refs.dashboard.load(clone(data));
			},

			// ── Chargement ────────────────────────────────────────

			async load(data) {
				const self = this;
				this.config = data.config || {};
				await this.update_user_table();

				this.title  = data.title;
				this.active = data.active !== undefined ? !!data.active : false;
				this.order  = data.order  !== undefined ? data.order  : 0;
				this.blocks = data.blocks || [];
				this.lines  = data.lines  || [];

				// S'assurer que blocks et lines ont la même longueur
				while (this.lines.length < this.blocks.length) {
					this.lines.push({ uid: this.$uuid.v4(), conditions: {} });
				}
				// Stocker le line_uid dans chaque entrée blocks pour resync après drag
				for (let i = 0; i < this.blocks.length; i++) {
					this.blocks[i].line_uid = this.lines[i].uid;
				}

				self.$nextTick(async function () {
					for (let i = 0; i < self.blocks.length; i++) {
						for (let j = 0; j < self.blocks[i].blocks.length; j++) {
							const ref = self.$refs[self.blocks[i].blocks[j].uid];
							if (ref && ref[0]) {
								await ref[0].load(self.blocks[i].blocks[j].params);
							}
						}
					}
					for (let i = 0; i < self.lines.length; i++) {
						const ref = self.$refs['query' + self.lines[i].uid];
						if (ref && ref[0]) {
							await ref[0].load(self.lines[i].conditions);
						}
					}
				});

				await this.$refs.dashboard.load(data);
			},

			// ── Sauvegarde ────────────────────────────────────────

			async save() {
				for (let i = 0; i < this.blocks.length; i++) {
					for (let j = 0; j < this.blocks[i].blocks.length; j++) {
						const ref = this.$refs[this.blocks[i].blocks[j].uid];
						if (ref && ref[0]) {
							this.blocks[i].blocks[j].params = await ref[0].save();
						}
					}
				}
				for (let i = 0; i < this.lines.length; i++) {
					const ref = this.$refs['query' + this.lines[i].uid];
					if (ref && ref[0]) {
						this.lines[i].conditions = await ref[0].save();
					}
				}
				const meta = { active: this.active, order: this.order };
				return {
					title:  this.title,
					blocks: this.blocks,
					lines:  this.lines,
					config: this.config,
					...meta,
					data_db: meta
				};
			},

			// ── Ajout ─────────────────────────────────────────────

			// Crée une ligne + un bloc et sélectionne ce bloc
			async add_line_with_block() {
				const lineUid  = this.$uuid.v4();
				const blockUid = this.$uuid.v4();
				this.blocks.push({ uid: lineUid, line_uid: null, blocks: [{ uid: blockUid, name: "", type: "", size: "", params: {} }] });
				const newLine = { uid: this.$uuid.v4(), conditions: {} };
				this.lines.push(newLine);
				this.blocks[this.blocks.length - 1].line_uid = newLine.uid;
				await this.$nextTick();
				this.view_id = blockUid;
			},

			// Ajoute un bloc dans une ligne existante et le sélectionne
			async addAndSelect(id_line) {
				const uid = this.$uuid.v4();
				this.blocks[id_line].blocks.push({ uid, name: "", type: "", size: "", params: {} });
				await this.$nextTick();
				this.view_id = uid;
			},

			// Kept for backward-compat (appelé depuis load/save externe)
			async add_line() {
				await this.add_line_with_block();
			},
			async add(id_line) {
				await this.addAndSelect(id_line);
			},

			// ── Suppression avec confirmation ─────────────────────

			askDel(id_line, idx) {
				const name = this.blocks[id_line].blocks[idx].name || "ce bloc";
				this.confirm_message  = `Supprimer le bloc « ${name} » ? Cette action est irréversible.`;
				this.confirm_callback = () => this.del(id_line, idx);
				this.$refs.confirm_modal.show();
			},
			askDelLine(id_line) {
				const count = this.blocks[id_line].blocks.length;
				this.confirm_message  = `Supprimer la ligne ${id_line + 1} et ses ${count} bloc(s) ? Cette action est irréversible.`;
				this.confirm_callback = () => this.del_line(id_line);
				this.$refs.confirm_modal.show();
			},
			doConfirm() {
				if (this.confirm_callback) {
					this.confirm_callback();
					this.confirm_callback = null;
				}
			},

			del(id_line, idx) {
				const uid = this.blocks[id_line].blocks[idx].uid;
				this.blocks[id_line].blocks.splice(idx, 1);
				if (this.view_id === uid) this.view_id = null;
				// Si la ligne est vide, la supprimer automatiquement
				if (this.blocks[id_line].blocks.length === 0) {
					this.lines.splice(id_line, 1);
					this.blocks.splice(id_line, 1);
				}
			},
			del_line(id_line) {
				const lineUid = this.lines[id_line] && this.lines[id_line].uid;
				if (this.view_id === lineUid) this.view_id = null;
				// Annuler la sélection si elle était dans cette ligne
				for (const block of this.blocks[id_line].blocks) {
					if (this.view_id === block.uid) this.view_id = null;
				}
				this.lines.splice(id_line, 1);
				this.blocks.splice(id_line, 1);
			},

			// ── Drag-and-drop ─────────────────────────────────────

			// Après déplacement d'un bloc entre lignes : nettoyer les lignes vides
			onBlockDragEnd() {
				for (let i = this.blocks.length - 1; i >= 0; i--) {
					if (this.blocks[i].blocks.length === 0) {
						this.lines.splice(i, 1);
						this.blocks.splice(i, 1);
					}
				}
			},

			// Dépôt dans la zone "nouvelle ligne"
			onDropToNewLine() {
				if (this.new_line_blocks.length === 0) return;
				const dropped = this.new_line_blocks.splice(0, this.new_line_blocks.length);
				const lineUid = this.$uuid.v4();
				const newLine = { uid: this.$uuid.v4(), conditions: {} };
				this.blocks.push({ uid: lineUid, line_uid: newLine.uid, blocks: dropped });
				this.lines.push(newLine);
				this.$nextTick(() => {
					if (dropped.length > 0) this.view_id = dropped[0].uid;
				});
			},

			// Après déplacement d'une ligne entière : resynchroniser lines[] sur blocks[]
			onLineDragEnd() {
				const linesMap = {};
				for (const l of this.lines) linesMap[l.uid] = l;
				this.lines = this.blocks.map(b => linesMap[b.line_uid] || { uid: this.$uuid.v4(), conditions: {} });
				// Mettre à jour line_uid
				for (let i = 0; i < this.blocks.length; i++) {
					this.blocks[i].line_uid = this.lines[i].uid;
				}
			},

			// ── Table utilisateur ─────────────────────────────────

			async update_user_table() {
				if (this.config.user_table) {
					const columns = await database.get("show", this.config.user_table);
					this.columns = columns.map(c => c.Field);
				} else {
					this.columns = ["groups", "env", "nom", "prenom", "identifiant"];
					const client = store.state.client;
					if (client.details && client.details.profile) {
						for (const p of client.details.profile) {
							this.columns.push(p.name);
						}
					}
				}
			},
		},
		mounted: async function () {
			const client = store.state.client;

			if (client.details && client.details.profile) {
				for (const p of client.details.profile) {
					this.columns.push(p.name);
				}
			}

			const tables = (client.details && client.details.tables) || [];
			this.tables = ["", ...tables.map(t => t.name)];
		},
	};
</script>

<style scoped>
/* ── Conteneur principal ───────────────────────────────────────── */
.dbb {
	font-family: inherit;
}

/* Mode "test en place" du générateur de code (TODO_CODEGEN_CHAT.md section 10) :
   verrouille la navigation entre blocs/lignes et les champs de configuration du
   bloc en cours de test, sans bloquer le panneau du générateur lui-même (rendu
   séparément dans textBuilder/vueComponentBuilder) ni la barre "Prévisualiser". */
.dbb-locked {
	pointer-events: none;
	opacity: 0.5;
	filter: grayscale(60%);
}

/* ── Barre d'actions persistante ──────────────────────────────── */
.dbb-topbar {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 16px;
	margin-bottom: 12px;
}

/* ── Config (accordéon) ────────────────────────────────────────── */
.dbb-config {
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	margin-bottom: 16px;
	overflow: hidden;
}
.dbb-config-toggle {
	padding: 10px 14px;
	background: #f7f7f7;
	cursor: pointer;
	font-weight: 600;
	user-select: none;
}
.dbb-config-toggle:hover { background: #efefef; }
.dbb-config-body {
	padding: 14px;
	border-top: 1px solid #e0e0e0;
}
.dbb-config-user-table {
	margin-top: 10px;
	padding-left: 12px;
	border-left: 3px solid #dee2e6;
}
.dbb-config-row {
	display: flex;
	gap: 24px;
	align-items: flex-start;
	margin-bottom: 8px;
}
.dbb-field {
	margin-bottom: 12px;
}
.dbb-field label {
	display: block;
	font-size: 0.82rem;
	font-weight: 600;
	color: #555;
	margin-bottom: 4px;
}

/* ── Layout main ───────────────────────────────────────────────── */
.dbb-main {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

/* ── Canvas ────────────────────────────────────────────────────── */
.dbb-canvas {
	width: 100%;
	min-width: 0;
}
.dbb-lines {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

/* État vide */
.dbb-empty {
	text-align: center;
	padding: 48px 24px;
	border: 2px dashed #ccc;
	border-radius: 10px;
	color: #888;
}
.dbb-empty-icon {
	font-size: 2.5rem;
	margin-bottom: 12px;
	color: #bbb;
}

/* ── Ligne ─────────────────────────────────────────────────────── */
.dbb-line {
	border: 1px solid #dde;
	border-radius: 8px;
	overflow: hidden;
	background: #fafafa;
}
.dbb-line-header {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 5px 10px;
	background: #eef0f5;
	border-bottom: 1px solid #dde;
	font-size: 0.82rem;
}
.dbb-line-handle {
	cursor: grab;
	color: #aaa;
	font-size: 1rem;
}
.dbb-line-handle:active { cursor: grabbing; }
.dbb-line-label {
	font-weight: 600;
	color: #555;
}
.dbb-line-filter-active {
	color: #0070cc;
	font-size: 0.75rem;
}
.dbb-line-actions {
	margin-left: auto;
	display: flex;
	gap: 0;
}
.dbb-line-actions .btn-link { color: #666; padding: 2px 6px; }
.dbb-line-actions .btn-link:hover { color: #333; }
.dbb-btn-danger.btn-link { color: #c0392b !important; }
.dbb-btn-active { color: #0070cc !important; }

/* ── Rangée de blocs ───────────────────────────────────────────── */
.dbb-blocks-row {
	display: flex;
	min-height: 70px;
	padding: 8px;
	gap: 8px;
}
.dbb-blocks-row--empty {
	border: 2px dashed #d0d0d0;
	background: #f5f5f5;
	justify-content: center;
	align-items: center;
	color: #bbb;
	font-size: 0.8rem;
}

/* ── Bloc ──────────────────────────────────────────────────────── */
.dbb-block {
	min-width: 60px;
	border-radius: 6px;
	padding: 8px 10px;
	background: #fff;
	border: 2px solid #dde;
	cursor: pointer;
	transition: border-color .15s, box-shadow .15s;
	display: flex;
	flex-direction: column;
	gap: 4px;
	position: relative;
}
.dbb-block:hover {
	border-color: #97b5e0;
	box-shadow: 0 1px 6px rgba(0,100,200,.1);
}
.dbb-block--selected {
	border-color: #0070cc !important;
	box-shadow: 0 0 0 3px rgba(0,112,204,.18) !important;
}
.dbb-block-name {
	font-size: 0.82rem;
	font-weight: 500;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.dbb-block-size-hint {
	font-size: 0.7rem;
	color: #aaa;
}

/* Types de blocs : couleurs */
.dbb-block--text    { border-left: 4px solid #2980b9; }
.dbb-block--db      { border-left: 4px solid #27ae60; }
.dbb-block--actions { border-left: 4px solid #e67e22; }
.dbb-block--notifs  { border-left: 4px solid #8e44ad; }
.dbb-block--button  { border-left: 4px solid #16a085; }
.dbb-block--vue_component { border-left: 4px solid #e74c3c; }
.dbb-block--none    { border-left: 4px solid #bbb; }

/* ── Badges type ───────────────────────────────────────────────── */
.dbb-type-badge {
	display: inline-block;
	font-size: 0.65rem;
	font-weight: 700;
	padding: 1px 5px;
	border-radius: 3px;
	text-transform: uppercase;
	background: #eee;
	color: #555;
}
.dbb-type-badge--text    { background: #d6eaf8; color: #1a5276; }
.dbb-type-badge--db      { background: #d5f5e3; color: #1e8449; }
.dbb-type-badge--actions { background: #fdebd0; color: #7d6608; }
.dbb-type-badge--notifs  { background: #e8daef; color: #6c3483; }
.dbb-type-badge--button  { background: #d1f2eb; color: #0e6655; }
.dbb-type-badge--vue_component { background: #fadbd8; color: #922b21; }

/* ── Zone nouvelle ligne ───────────────────────────────────────── */
.dbb-new-line-zone {
	margin-top: 10px;
	min-height: 44px;
	border: 2px dashed #c0cfe0;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background .2s;
}
.dbb-new-line-zone:hover { background: #eef4fb; }
.dbb-new-line-hint {
	color: #aab8c8;
	font-size: 0.8rem;
	pointer-events: none;
}

/* ── Barre d'outils canvas ─────────────────────────────────────── */
.dbb-canvas-toolbar {
	margin-top: 12px;
	display: flex;
	align-items: center;
	gap: 8px;
}

/* ── Panneau éditeur ───────────────────────────────────────────── */
.dbb-editor {
	width: 100%;
	border: 1px solid #dde;
	border-radius: 8px;
	background: #fff;
}
.dbb-editor-placeholder {
	padding: 32px 20px;
	text-align: center;
	color: #bbb;
	font-size: 0.88rem;
}
.dbb-editor-placeholder i { font-size: 1.5rem; display: block; margin-bottom: 8px; }
.dbb-editor-header {
	padding: 10px 14px;
	background: #f0f4f8;
	border-bottom: 1px solid #dde;
	font-weight: 600;
	font-size: 0.9rem;
	display: flex;
	align-items: center;
	gap: 8px;
}
.dbb-editor-body {
	padding: 14px;
}
.dbb-params-title {
	margin-top: 16px;
	padding-top: 12px;
	border-top: 1px solid #eee;
	font-size: 0.82rem;
	text-transform: uppercase;
	color: #888;
	letter-spacing: .05em;
}
.dbb-editor-del {
	margin-top: 20px;
	padding-top: 12px;
	border-top: 1px solid #eee;
}

/* ── Sélecteur de taille ───────────────────────────────────────── */
.dbb-size-selector {
	display: flex;
	gap: 3px;
	margin-bottom: 4px;
}
.dbb-size-cell {
	flex: 1;
	height: 22px;
	border-radius: 3px;
	background: #e9ecef;
	cursor: pointer;
	transition: background .12s;
	border: 1px solid #dee2e6;
}
.dbb-size-cell--on { background: #0070cc; border-color: #005fa3; }
.dbb-size-cell--hover { background: #55a0e0; border-color: #3d8fce; }
.dbb-size-cell--hover-off { background: #c8dff5; border-color: #a8c8e8; }
</style>
